import { useEffect, useRef } from "react";
import { recordSession } from "./analytics";
import { SALAS_CONTROLABLES } from "./salas";

/*
 * Visitas a una SALA, no solo a una lección o a un juego.
 *
 * POR QUÉ. `usage_sessions` solo se llenaba desde LessonPlayer y Juegos. El
 * kiosco entra por Servicios y vive en Servicios, Videoteca, Universidad y
 * Empleo: nada de eso dejaba rastro, así que un kiosco muy usado se veía en el
 * panel igual que uno apagado. Con esto cada paso por una sala queda como una
 * sesión `sala-<clave>`, y el panel puede decir cuánto se usó y en qué.
 *
 * La DURACIÓN va hasta la última interacción, no hasta que se sale. Un kiosco
 * que se queda solo cinco minutos antes de reiniciarse no son cinco minutos de
 * uso; sin esto el tiempo de pantalla abandonada inflaría las horas.
 */

const MIN_SEGUNDOS = 3;       // un rebote de redirección no es una visita
const MAX_SEGUNDOS = 30 * 60; // tope por visita: nadie está media hora sin tocar nada

/** "/servicios/aaa" → "servicios"; null si la ruta no es una sala. */
export function salaDeRuta(pathname: string): string | null {
  const primera = pathname.split("/").filter(Boolean)[0] ?? "";
  const clave = primera === "juegos" || primera === "recursos" ? "stem" : primera;
  return (SALAS_CONTROLABLES as readonly string[]).includes(clave) ? clave : null;
}

export function useRegistroDeSalas(pathname: string, activo: boolean, perfil: string): void {
  const visita = useRef<{ sala: string; inicio: number; ultima: number } | null>(null);

  // Cierra la visita en curso y la manda a la cola de analítica.
  const cerrar = useRef(() => {
    const v = visita.current;
    visita.current = null;
    if (!v) return;
    const segundos = Math.min(Math.round((v.ultima - v.inicio) / 1000), MAX_SEGUNDOS);
    if (segundos < MIN_SEGUNDOS) return;
    recordSession({
      profile: perfil,
      moduleSlug: `sala-${v.sala}`,
      startedAt: new Date(v.inicio).toISOString(),
      durationSeconds: segundos,
      result: "visited",
      meta: { sala: v.sala },
    });
  });

  // Cambio de ruta: se cierra la anterior y se abre la nueva, si es una sala.
  useEffect(() => {
    if (!activo) return;
    const sala = salaDeRuta(pathname);
    if (visita.current?.sala === sala) return; // dentro de la misma sala (una materia, un detalle)
    cerrar.current();
    if (sala) {
      const ahora = Date.now();
      visita.current = { sala, inicio: ahora, ultima: ahora };
    }
  }, [pathname, activo]);

  // Interacción: estira la visita. Salida de la página: la cierra.
  useEffect(() => {
    if (!activo) return;
    const tocar = () => { if (visita.current) visita.current.ultima = Date.now(); };
    const salir = () => cerrar.current();
    const eventos = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    for (const e of eventos) window.addEventListener(e, tocar, { passive: true });
    window.addEventListener("pagehide", salir);
    return () => {
      for (const e of eventos) window.removeEventListener(e, tocar);
      window.removeEventListener("pagehide", salir);
      cerrar.current(); // logout o desmontaje
    };
  }, [activo]);
}
